import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Submit.css';

const SubmitPage = () => {
  // State for form fields
  const [email, setEmail] = useState('');
  const [originalPaper, setOriginalPaper] = useState('');
  const [dockingMethod, setDockingMethod] = useState('');
  const [resultsFile, setResultsFile] = useState(null);

  // State for UI and a single object for all errors
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Ref for the hidden file input
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE_MB = 50;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  // --- File size is checked here on selection ---
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    const newErrors = { ...errors };

    if (!file) {
      setResultsFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      newErrors.file = `File size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`;
      setResultsFile(null); // Clear the invalid file selection
    } else {
      delete newErrors.file; // Clear previous file error on valid selection
      setResultsFile(file);
    }
    setErrors(newErrors);
  };

  // --- Main Validation Logic ---
  const validateForm = () => {
    const newErrors = {};

    // 1. Email validation
    if (!email) {
      newErrors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(email)) { // Simple regex for email format
      newErrors.email = 'Please enter a valid email format.';
    }

    // 2. Original Paper validation
    if (!originalPaper) {
      newErrors.originalPaper = 'Original paper is required.';
    }

    // 3. Docking Method validation
    if (!dockingMethod) {
      newErrors.dockingMethod = 'Name of the docking method is required.';
    }

    // 4. File validation
    if (!resultsFile) {
      newErrors.file = 'A results file is required.';
    }

    setErrors(newErrors);
    
    // Return true if the errors object is empty, false otherwise
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (event) => { // Make the function async
    event.preventDefault();

    if (!validateForm()) {
      console.log('Validation failed. Form not submitted.');
      return;
    }

    // Set a loading state here if you want (optional)
    // setIsLoading(true);

    const formData = new FormData();
    formData.append('email', email);
    formData.append('originalPaper', originalPaper);
    formData.append('dockingMethod', dockingMethod);
    formData.append('results', resultsFile);

    try {
      // --- THIS IS THE KEY PART ---
      // Send the data to your backend server
      const response = await fetch('/api/submit', {
        method: 'POST',
        body: formData, // FormData is sent as the body
        // Note: No 'Content-Type' header is needed; the browser sets it automatically for FormData
      });

      if (!response.ok) {
        // If the server responds with an error (e.g., status 400 or 500)
        const errorData = await response.json();
        // You could set an error message state here based on errorData.message
        console.error('Submission failed:', errorData.message);
        // setIsLoading(false);
        return; // Stop execution
      }

      // If the submission was successful
      const result = await response.json();
      console.log('Server response:', result);
      setIsSubmitted(true); // Show the success message

    } catch (error) {
      console.error('An error occurred while submitting the form:', error);
      // Handle network errors, etc.
      // You could set a general error message state here
    } finally {
      // setIsLoading(false); // Stop the loading indicator
    }
  };
  
  const handleCustomButtonClick = () => {
    fileInputRef.current.click();
  };
  
  // A helper function to clear errors as the user types
  const clearError = (fieldName) => {
    if (errors[fieldName]) {
        const newErrors = { ...errors };
        delete newErrors[fieldName];
        setErrors(newErrors);
    }
  };

  if (isSubmitted) {
    return (
      <div className="submit-container">
        <h1>Submission Successful!</h1>
        <p>We will review the results as soon as possible.</p>
        <Link to="/leaderboard" className="back-button">Go Back to Leaderboards</Link>
      </div>
    );
  }

  return (
    <div className="submit-container">
      <h1>Submit Your Results</h1>
      <p>Please provide the following information to submit your docking results.</p>
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="email">Your Email:</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => {
                setEmail(e.target.value);
                clearError('email');
            }}
            required
          />
          {errors.email && <p className="error-message">{errors.email}</p>}
        </div>
        <div className="form-group">
          <label htmlFor="originalPaper">Original Paper of the Results:</label>
          <input
            type="text"
            id="originalPaper"
            value={originalPaper}
            onChange={(e) => {
                setOriginalPaper(e.target.value);
                clearError('originalPaper');
            }}
            required
          />
          {errors.originalPaper && <p className="error-message">{errors.originalPaper}</p>}
        </div>
        <div className="form-group">
          <label htmlFor="dockingMethod">Name of the Docking Method:</label>
          <input
            type="text"
            id="dockingMethod"
            value={dockingMethod}
            onChange={(e) => {
                setDockingMethod(e.target.value);
                clearError('dockingMethod');
            }}
            required
          />
          {errors.dockingMethod && <p className="error-message">{errors.dockingMethod}</p>}
        </div>
        <div className="form-group">
          <label>The Results (in a .zip file):</label>
          <p className="description">
            Please provide the extracted output in zip format after the "extract model outputs" step in{' '}
            <a href="https://github.com/CataAI/posex" target="_blank" rel="noopener noreferrer">
              PoseX
            </a>.
          </p>
          <input
            type="file"
            id="results"
            accept=".zip"
            onChange={handleFileSelect}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          <div className="file-input-custom-container">
            <button type="button" className="file-input-button" onClick={handleCustomButtonClick}>
              Choose File
            </button>
            <span className="file-name">{resultsFile ? resultsFile.name : 'No file chosen'}</span>
          </div>
          {errors.file && <p className="error-message">{errors.file}</p>}
        </div>
        <div className="form-actions">
          <Link to="/leaderboard" className="back-button">Back to Leaderboards</Link>
          <button type="submit" className="submit-button">Submit</button>
        </div>
      </form>
    </div>
  );
};

export default SubmitPage;